/**
 * Page color context — provides the active page's color palette to the entire tree.
 *
 * Components use `usePageColors()` instead of importing `pageColors.xxx` directly,
 * so link colors, accents, and borders automatically match the current route.
 */

import { createContext, useContext, useMemo } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useTheme } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { getPageColors, type PageColorConfig } from '@/theme/colors';

interface PageColorContextValue {
  colors: PageColorConfig;
  accent: string;
  isDark: boolean;
}

const PageColorContext = createContext<PageColorContextValue | null>(null);

export function usePageColors(): PageColorContextValue {
  const ctx = useContext(PageColorContext);
  if (!ctx) {
    throw new Error('usePageColors must be used within a PageColorProvider');
  }
  return ctx;
}

/**
 * Optional: safe version that returns undefined outside the provider
 * (useful in shared components that may render during SSR before router is ready).
 */
export function usePageColorsSafe(): PageColorContextValue | null {
  return useContext(PageColorContext);
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function PageColorProvider({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const colors = useMemo(() => getPageColors(pathname), [pathname]);
  const accent = isDark ? colors.accent : colors.primary;

  const value = useMemo<PageColorContextValue>(
    () => ({ colors, accent, isDark }),
    [colors, accent, isDark],
  );

  const globalLinkStyles = useMemo(
    () => ({
      ':root': {
        '--page-primary': colors.primary,
        '--page-secondary': colors.secondary,
        '--page-accent': colors.accent,
        '--page-color': accent,
        '--page-color-muted': hexToRgba(accent, 0.3),
      },
      'a:not(.MuiButtonBase-root):not([class*="MuiIconButton"]):not([data-no-page-color])': {
        color: accent,
        textDecorationColor: hexToRgba(accent, 0.3),
        transition: 'color 0.2s, text-decoration-color 0.2s',
        '&:hover': {
          textDecorationColor: accent,
        },
      },
    }),
    [colors, accent],
  );

  return (
    <PageColorContext.Provider value={value}>
      <GlobalStyles styles={globalLinkStyles} />
      {children}
    </PageColorContext.Provider>
  );
}
